import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Link from "next/link";
import { IoHomeOutline, IoCartOutline, IoPersonOutline, IoLogOutOutline } from "react-icons/io5";
import { MdOutlineLocationOn } from "react-icons/md";
import { AiOutlineShop } from "react-icons/ai";
import { ROUTES } from "@utils/routes";
import { useLogoutMutation } from "@framework/auth/use-logout";
import { useTranslation } from "next-i18next";
import { FaWarehouse } from "react-icons/fa";
import UserDashboardCard from "./dashboard/user-card";
import { RiTeamFill } from 'react-icons/ri';
import { getCurrentLocationAndLanguage } from '@utils/location';
interface MenuItem {
  slug: string;
  name: string;
  Icon: React.ComponentType<{
    className?: string;
  }>;
}
const accountMenu: MenuItem[] = [{
  slug: ROUTES.ACCOUNT,
  name: "text-dashboard",
  Icon: IoHomeOutline
}, {
  slug: ROUTES.ORDERS,
  name: "text-orders",
  Icon: IoCartOutline
}, {
  slug: ROUTES.MY_ADDRESS,
  name: "text-my-address",
  Icon: MdOutlineLocationOn
}, {
  slug: ROUTES.RETAIL,
  name: "text-retail",
  Icon: AiOutlineShop
}, {
  slug: ROUTES.WAREHOUSE_LIMITS,
  name: "text-page-warehouse-limits",
  Icon: FaWarehouse
}, ...(getCurrentLocationAndLanguage().location === 'tr' ? [{
  slug: ROUTES.TEAM_MEMBER,
  name: "text-team-member",
  Icon: RiTeamFill
}] : []), {
  slug: ROUTES.ACCOUNT_DETAILS,
  name: "text-account-details",
  Icon: IoPersonOutline
}
// {
//   slug: ROUTES.CHANGE_PASSWORD,
//   name: "text-change-password",
//   Icon: IoSettingsOutline,
// },
];
const menuContainerClasses = "fixed bottom-14 sm:bottom-16 left-0 right-0 p-5 bg-white dark:bg-zinc-950 backdrop-blur-lg rounded-t-2xl shadow-lg z-50 md:hidden";
const menuItemClasses = "flex items-center p-3 bg-neutral-100 dark:bg-neutral-800 rounded-xl hover:bg-neutral-200 transition-colors";
const logoutButtonClasses = "flex items-center p-3 bg-red-600 rounded-xl shadow-md hover:bg-red-700 transition-colors";
const backdropClasses = "fixed top-0 left-0 right-0 bottom-14 sm:bottom-16 bg-black bg-opacity-30 backdrop-blur-sm z-40";
const menuVariants = {
  open: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5
    }
  },
  closed: {
    opacity: 0,
    y: 20,
    transition: {
      duration: 0.5
    }
  }
};
const menuItemVariants = {
  open: (i: number) => ({
    scale: 1,
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 260,
      damping: 20,
      delay: 0.1 + (accountMenu.length - i) * 0.10
    }
  }),
  closed: {
    scale: 0,
    opacity: 0,
    y: 20,
    transition: {
      duration: 0.8
    }
  }
};
interface MobileDashboardNavProps {
  isOpen: boolean;
  toggleMenu: () => void;
}
const MobileDashboardNav: React.FC<MobileDashboardNavProps> = ({
  isOpen,
  toggleMenu
}) => {
  const {
    mutate: logout
  } = useLogoutMutation();
  const {
    t
  } = useTranslation("common");
  const menuRef = useRef<HTMLDivElement>(null);
  const [isShortScreen, setIsShortScreen] = useState(false);
  useEffect(() => {
    const checkHeight = () => setIsShortScreen(window.innerHeight < 700);
    checkHeight();
    window.addEventListener('resize', checkHeight);
    return () => window.removeEventListener('resize', checkHeight);
  }, []);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        toggleMenu();
      }
    };
    const handleScroll = () => {
      toggleMenu();
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("scroll", handleScroll);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("scroll", handleScroll);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("scroll", handleScroll);
    };
  }, [isOpen, toggleMenu]);
  return <AnimatePresence data-sentry-element="AnimatePresence" data-sentry-component="MobileDashboardNav" data-sentry-source-file="mobile-dashboard-nav.tsx">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className={backdropClasses} onClick={toggleMenu} />
          <motion.div ref={menuRef} initial="closed" animate="open" exit="closed" variants={menuVariants} className={menuContainerClasses}>
            <UserDashboardCard />
            <ul className="grid gap-4 mt-4">
              {accountMenu.map((item, idx) => <motion.li key={item.slug} custom={idx} variants={menuItemVariants}>
                  <Link href={item.slug} passHref onClick={toggleMenu} className={menuItemClasses} aria-label={t(item.name)}>

                    <item.Icon className="w-4 h-4 text-neutral-800 dark:text-neutral-200" />
                    <span className={`pl-3 ${isShortScreen ? 'text-xs' : 'text-sm'} font-medium text-neutral-800 dark:text-neutral-200`}>
                      {t(item.name)}
                    </span>

                  </Link>
                </motion.li>)}
              <motion.button onClick={() => {
            logout();
            toggleMenu();
          }} custom={accountMenu.length} variants={menuItemVariants} className={logoutButtonClasses} aria-label={t("text-logout")}>
                <IoLogOutOutline className="w-4 h-4 text-neutral-100 dark:text-neutral-200" />
                <span className={`pl-3 ${isShortScreen ? 'text-xs' : 'text-sm'} font-medium text-neutral-100 dark:text-neutral-200`}>
                  {t("text-logout")}
                </span>
              </motion.button>
            </ul>
          </motion.div>
        </>}
    </AnimatePresence>;
};
export default MobileDashboardNav;